import { BASE_URL, PRINT_SERVICE_API_KEY, PRINT_SERVICE_URL } from "@/constants/endpoints";
import {
  BackupCheckDetail,
  BackupCheckEntity,
  PrintCheckRequest,
} from "@/pages/requests/backup-checks/schema/backup-checks.schema";
import { createAxios, createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import { AxiosInstance, AxiosResponse } from "axios";

let BackupCheckApiPromise: Promise<AxiosInstance> | null = null;
let PrintCheckApiPromise: Promise<AxiosInstance> | null = null;

const getBackupCheckApi = async (): Promise<AxiosInstance | null> => {
  if (BackupCheckApiPromise) return BackupCheckApiPromise;
  BackupCheckApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return BackupCheckApiPromise;
};

const getPrintServiceApi = async (): Promise<AxiosInstance | null> => {
  if (PrintCheckApiPromise) return PrintCheckApiPromise;
  const api = createAxios(PRINT_SERVICE_URL);
  if (!api) return null;
  if (PRINT_SERVICE_API_KEY) {
    api.defaults.headers.common["x-api-key"] = PRINT_SERVICE_API_KEY;
  }
  PrintCheckApiPromise = Promise.resolve(api);
  return PrintCheckApiPromise;
};

export interface GetBackupChecksProps {
  fecha?: string;
  nroCuenta?: number;
  monto?: number;
  solicitante?: string;
  estados?: number[];
  "P.Pagina": number;
  "P.Records"?: number;
}

export interface GetBackupChecksResponse {
  listado: BackupCheckEntity[];
  totalPages: number;
  totalRecords: number;
}

export interface GetBackupCheckProps {
  id: number;
}

export interface PrintBackupCheckProps {
  id: number;
}

export interface CancelBackupCheckProps {
  id: number;
  idMotivo?: number;
  mensaje?: string;
}

export const getBackupChecks = async (
  props: GetBackupChecksProps,
): Promise<GetBackupChecksResponse | null> => {
  const api = await getBackupCheckApi();
  if (!api) return null;

  const response = await api.get(
    `/ChequesRespaldo?${objectToQueryStringWithArray(props)}`,
  );

  return response.data;
};

export interface FinalizeBackupCheckProps {
  id: number;
}

export const finalizeBackupCheck = async ({ id }: FinalizeBackupCheckProps) => {
  const api = await getBackupCheckApi();
  if (!api) return null;

  const response = await api.put(
    `/ChequesRespaldo/${id}/finalizar`,
    {},
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

export const getBackupCheck = async ({
  id,
}: GetBackupCheckProps): Promise<BackupCheckDetail | null> => {
  const api = await getBackupCheckApi();
  if (!api) return null;

  const response = await api.get(`/ChequesRespaldo/${id}`);
  return response.data;
};

export const getPrinterStatus = async (): Promise<AxiosResponse | null> => {
  const api = await getPrintServiceApi();
  if (!api) return null;
  
  const response = await api.get(`/status`);
  return response;
};

export const printBackupCheck = async (printRequest: PrintCheckRequest) => {
  const api = await getPrintServiceApi();
  if (!api) return null;

  const statusResponse = await api.post(
    `/print/printCheck`,
    printRequest,
  );
  return statusResponse.data;
};

export interface UpdateBackupCheckNumberProps {
  id: number;
  chequeNumber: string;
}

export const updateBackupCheckChequeNumber = async ({
  id,
  chequeNumber,
}: UpdateBackupCheckNumberProps) => {
  const api = await getBackupCheckApi();
  if (!api) return null;

  const response = await api.put(`/ChequesRespaldo/${id}/nro-cheque`, {
    nroCheque: chequeNumber,
  });
  return response.data;
};

export const cancelBackupCheck = async ({
  id,
  idMotivo,
  mensaje,
}: CancelBackupCheckProps) => {
  const api = await getBackupCheckApi();
  if (!api) return null;

  const response = await api.delete(
    `/ChequesRespaldo/${id}/cancelar?idMotivo=${idMotivo}&mensaje=${mensaje}`,
  );
  return response.data;
};
